import userAtom from "@/atoms/userAtom";
import Modal from "@/components/shared/ui/Modal";
import { MAX_FILE_SIZE } from "@/constants";
import useGetAdditionalInfo from "@/hooks/methodology/useGetAdditionalInfo";
import usePostAdditionalInfo from "@/hooks/methodology/usePostAdditionalInfo";
import useSubmitForPublish from "@/hooks/methodology/useSubmitForPublish";
import { BasicInformation } from "@/schemas/methodology/basicInformation";
import { MethodologyAdditionalInfo } from "@/types/AdditionalInfo";
import { MethodologyWithRoundDetails } from "@/types/Methodology";
import { UserRole } from "@/types/Role";
import { downloadFile } from "@/utils/downloadFile";
import { getAuthors } from "@/utils/getAuthors";
import { getFileSize } from "@/utils/getFileSize";
import { isReviewerOfGivenMethodology } from "@/utils/isOriginator";
import { getFileDownloadButtonLabel } from "@/utils/validationMessages";
import { useAtom } from "jotai";
import Link from "next/link";
import { useRouter } from "next/router";
import { ComponentProps, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { BsFileEarmarkText, BsPencil } from "react-icons/bs";
import { FaArrowLeft } from "react-icons/fa";
import DocumentUploadModal from "../projectDeveloper/DocumentUploadModal";
import InputField from "../shared/formFields/input";
import TextAreaField from "../shared/formFields/textarea";
import Button from "../shared/ui/Button";

type ConditionallyApprovedHeaderProps = {
	methodology: MethodologyWithRoundDetails;
	publishDisabled: boolean;
	additionalInfoId?: number;
	additionalInfoData?: MethodologyAdditionalInfo;
	isOriginator?: boolean;
	roundStatus?: string;
	refetchRound?: () => void;
	isForMethodologySelection?: boolean;
	isDisplayingForSubmittedProject?: boolean;
};

const ConditionallyApprovedMethodologyHeader = (props: ConditionallyApprovedHeaderProps) => {
	const { methodology, additionalInfoId, refetchRound } = props;
	const router = useRouter();
	const { query } = router;
	const methodologyId = query.id?.toString();
	const { data: methodologyWithAdditionalInfo } = useGetAdditionalInfo(methodologyId);
	const { mutate: updateExistingInfo } = usePostAdditionalInfo("Existing information", refetchRound);
	const [isPublishConfirmationModalOpen, setPublishConfirmationModalOpen] = useState(false);
	const [openDescription, setOpenDescription] = useState(false);
	const [openAuthor, setOpenAuthor] = useState(false);
	const [openFile, setOpenFile] = useState(false);
	const { mutate: submitMethodologyForPublish } = useSubmitForPublish();
	const [isDocumentUploadModalOpen, setIsDocumentUploadModalOpen] = useState(false);
	const [documentFile, setDocumentFile] = useState<File | null>(null);
	const snakeToSentenceCase = (inputString: string) => {
		return inputString.split("_").join(" ");
	};
	const [user] = useAtom(userAtom);
	const {
		register,
		handleSubmit,
		watch,
		reset,
		formState: { errors },
		trigger,
	} = useForm<BasicInformation>({
		defaultValues: {
			title: methodology?.title || "",
			authors: methodology?.authors.join(",") || "",
			overview: methodology?.overview || "",
			methodologyProposalFile: methodologyWithAdditionalInfo?.proposalFile || "",
		},
	});

	const isMC = props.isOriginator;
	const isReviewer = user && methodology && isReviewerOfGivenMethodology(user, methodology);
	const isReviewerAndPublished = isReviewer && methodology?.status === "APPROVED";

	const isAdmin = user?.roles?.includes(UserRole.ADMIN);
	const isAdminAndPublished = isAdmin && methodology?.status === "APPROVED";

	const canEditData =
		methodology?.status === "CONDITIONALLY_APPROVED" &&
		props.isOriginator &&
		!(props.additionalInfoData?.status === "SUBMITTED");
	// if page is being used for display in submitted project
	const { isDisplayingForSubmittedProject } = props;
	// if page is being used for methodology selection
	const { isForMethodologySelection } = props;
	const redirectProjectId = router.query.redirectProjectId;

	const authorWatch = watch("authors");
	const closeModal = () => {
		setPublishConfirmationModalOpen(false);
	};

	const onModalConfirm = () => {
		if (additionalInfoId) {
			submitMethodologyForPublish(additionalInfoId);
		}
	};

	useEffect(() => {
		setDocumentFile(null);
	}, []);

	const handleUploadDocument = () => {
		if (!documentFile) {
			setIsDocumentUploadModalOpen(true);
			setOpenFile(!openFile);
		} else setDocumentFile(null);
	};

	const onSubmit = (data: any) => {
		const formData: any = new FormData();
		if (methodologyWithAdditionalInfo?.methodologyAdditionalInfo?.id) {
			formData.append("methodologyAdditionalInfoId", methodologyWithAdditionalInfo?.methodologyAdditionalInfo?.id);
		}
		formData.append("methodologyId", methodologyId!!);
		if (openAuthor) {
			formData.append("methodologyInfo[authors]", data.authors);
			updateExistingInfo(formData);
			setOpenAuthor(false);
		}
		if (openDescription) {
			formData.append("methodologyInfo[overview]", data.overview);
			updateExistingInfo(formData);
			setOpenDescription(false);
		}
		if (openFile) {
			formData.append("methodologyProposalFile", documentFile!!);
			updateExistingInfo(formData);
			setDocumentFile(null);
			setOpenFile(false);
		}
	};

	const handleUseMethodologyForProject = () => {
		// in case of a  project that has id
		if (redirectProjectId) {
			if (router.query.project) {
				const query = router.query;
				delete query.roundId;
				router.push({
					pathname: `/projects/my-projects/${encodeURIComponent(router.query.project as string)}/${
						router.query.isEditModeInValidation ? "edit" : "draft"
					}`,
					query: {
						...router.query,
						id: router.query.project,
						selectedMethodologyId: methodology.id,
						redirectProjectId: redirectProjectId,
					},
				});
				return;
			} else {
				// in case of new project
				router.push(
					`/projects/new-project-proposal?selectedMethodologyId=${methodology.id}&redirectProjectId=${redirectProjectId}`,
				);
			}
		}
	};

	const handleGoBackToProjectWithoutChoosing = () => {
		if (redirectProjectId) {
			// in case of a  project that has id
			if (router.query.project) {
				const query = router.query;
				delete query.roundId;
				router.push({
					pathname: `/projects/my-projects/${encodeURIComponent(router.query.project as string)}/${
						router.query.isEditModeInValidation ? "edit" : "draft"
					}`,
					query: {
						...router.query,
						id: router.query.project,
						redirectProjectId: redirectProjectId,
					},
				});
				return;
			} else {
				// in case of new project
				router.push(`/projects/new-project-proposal?redirectProjectId=${redirectProjectId}`);
			}
		}
	};

	const handleGoBackToSumittedProject = () => {
		const { projectStatus, originatorId } = router.query;
		if (router.query.isRegistryView) {
			if (projectStatus === "IN_VALIDATION") {
				return router.push(`/registry/in-validation/${router.query.project}`);
			}
			return router.push(`/registry/${router.query.project}`);
		}
		if (isAdmin) {
			if (projectStatus === "SUBMITTED") {
				return router.push(`/admin/tools/project-review-panels/${router.query.project}/submitted`);
			} else if (projectStatus === "IN_VALIDATION") {
				return router.push(`/admin/tools/project-review-panels/${router.query.project}/view`);
			} else if (projectStatus === "VALIDATED") {
				return router.push(`/admin/tools/project-review-panels/${router.query.project}/view`);
			}
		} else if (originatorId == user?.id) {
			if (projectStatus === "SUBMITTED") {
				return router.push(`/projects/my-projects/${router.query.project}/submit`);
			} else if (projectStatus === "VALIDATED") {
				return router.push(`/projects/my-projects/${router.query.project}/submit`);
			}
		} else {
			if (projectStatus === "IN_VALIDATION") {
				return router.push(`/projects/projects-in-validation/${router.query.project}/view`);
			} else if (projectStatus === "VALIDATED") {
				return router.push(`/projects/projects-in-validation/${router.query.project}/view`);
			}
		}
	};

	const handleGobackToSubmittedProjectOrProjectProposal = () => {
		if (isDisplayingForSubmittedProject) {
			handleGoBackToSumittedProject();
		} else {
			handleGoBackToProjectWithoutChoosing();
		}
	};
	const handleCancle = () => {
		reset();
	};

	return (
		<>
			<aside className="absolute mt-[-14px] pb-0">
				{(isForMethodologySelection || isDisplayingForSubmittedProject) && (
					<Button
						className="flex items-center gap-3"
						variant="outline"
						size="small"
						onClick={handleGobackToSubmittedProjectOrProjectProposal}
					>
						<FaArrowLeft /> Go back to project
					</Button>
				)}
			</aside>

			<header className="flex justify-start align-middle">
				<form onSubmit={handleSubmit(onSubmit)} className="flex w-full justify-between">
					<div className="flex flex-1 flex-col gap-6">
						<h1>{methodology?.title}</h1>

						{methodology.status == "APPROVED" && (
							<div className="flex w-[95%] flex-col space-y-2 bg-clearSky-300 p-4 text-base ">
								<div className="text-black-300 flex w-fit items-center justify-center gap-2">
									<AiOutlineInfoCircle size="20px" />
									<h3>Disclaimer</h3>
								</div>

								<p className="text-base text-gray-700">
									All methodologies are grounded in the latest scientific advancements and carbon market best practices.
									The Expert Panel can only operate using knowledge and research available at the time which may lead to
									uncertainties or knowledge gaps, and this should be considered when assessing rapidly evolving sectors
									or previous versions of methodologies or projects.{" "}
									<Link
										href={
											"https://open-carbon-protocol.gitbook.io/open-carbon-protocol-knowledge-base/how-we-work/audit-process"
										}
										className={"text-blue-500 underline"}
									>
										See more here
									</Link>
									.
								</p>
							</div>
						)}

						<div>
							<span className="me-2 rounded-full bg-sand-300 px-2.5 py-0.5 text-xs font-medium">
								{(methodology?.status && snakeToSentenceCase(methodology.status)) ?? ""}
							</span>
						</div>
						{openAuthor ? (
							<div className="w-3/5">
								<InputField
									{...register("authors")}
									errorMessage={errors.authors?.message}
									label="Authors"
									type="text"
								/>

								<p className="my-2">
									{getAuthors(authorWatch).map((author, index) => (
										<span key={index} className="my-2 mr-2 rounded-full border border-secondary px-2 text-sm">
											{author}
										</span>
									))}
								</p>

								<div className="mt-3 flex gap-3">
									<Button
										variant="outline"
										size="small"
										onClick={() => {
											setOpenAuthor(!openAuthor);
											handleCancle();
										}}
									>
										Cancel
									</Button>
									<Button type="submit" size="small">
										Save Changes
									</Button>
								</div>
							</div>
						) : (
							<div>
								<div className="flex items-center gap-x-2">
									<h4>Authors</h4>
									{canEditData && (
										<Button
											onClick={() => setOpenAuthor(!openAuthor)}
											variant="transparent"
											className="bg-white px-2 text-gray-500"
										>
											<BsPencil className="text-sm" />
										</Button>
									)}
								</div>
								<p className="my-2">
									{methodology?.authors.map((author, index) => (
										<span key={index}>
											{author}
											{index + 1 !== methodology.authors.length && ",  "}
										</span>
									))}
								</p>
							</div>
						)}

						<div className="flex flex-row flex-wrap gap-2 text-xs font-semibold text-secondary">
							<p className="rounded-full border border-solid border-secondary px-2 py-2">
								{methodology?.sectoralScope.primary}
							</p>
							<Link
								className={`rounded-full border border-solid border-secondary px-2 py-2 ${
									[6, 31].includes(methodology?.sectoralScopeId) ? "cursor-pointer hover:bg-gray-50" : "!cursor-default"
								}`}
								href={
									[6, 31].includes(methodology?.sectoralScopeId)
										? `/sectoral-scopes/${methodology?.sectoralScopeId}`
										: "#"
								}
							>
								<p>{methodology?.sectoralScope?.secondary}</p>
							</Link>
						</div>
						{openDescription ? (
							<div className="flex flex-col">
								<TextAreaField
									{...register("overview")}
									errorMessage={errors.overview?.message}
									label="Description"
									rows={4}
								/>
								<div className="mt-3 flex gap-3">
									<Button variant="outline" size="small" onClick={() => setOpenDescription(!openDescription)}>
										Cancel
									</Button>
									<Button type="submit" size="small">
										Save Changes
									</Button>
								</div>
							</div>
						) : (
							<div className="flex flex-col ">
								<div className="flex items-center gap-x-2">
									<h2>Description</h2>
									{canEditData && (
										<Button
											onClick={() => setOpenDescription(!openDescription)}
											variant="transparent"
											className="bg-white px-2 text-gray-500"
										>
											<BsPencil className="text-sm" />
										</Button>
									)}
								</div>
								<div className="mt-2">{methodology?.overview}</div>
							</div>
						)}
					</div>

					{isForMethodologySelection && (
						<div className="px-5">
							<div className="mr-10 mt-0 flex flex-col space-y-5 border-2 border-gray-200 p-5">
								<span className="text-xl font-bold">Actions</span>
								<Button
									variant="outline"
									className="-ml-1 flex items-center space-x-3 font-medium"
									onClick={() =>
										methodologyWithAdditionalInfo?.proposalFile &&
										downloadFile(methodologyWithAdditionalInfo?.proposalFile)
									}
								>
									<span>
										<BsFileEarmarkText />
									</span>

									<span>{getFileDownloadButtonLabel(methodologyWithAdditionalInfo)}</span>
								</Button>

								<Button type="submit" variant="solid" onClick={handleUseMethodologyForProject}>
									Use This Methodology
								</Button>
							</div>
						</div>
					)}

					{!isForMethodologySelection && (
						<div className="flex flex-col gap-6">
							{(methodologyWithAdditionalInfo?.status == "APPROVED" ||
								isMC ||
								isAdminAndPublished ||
								isReviewerAndPublished) && (
								<Button
									variant="outline"
									className="-ml-1 flex items-center space-x-3 font-medium"
									onClick={() =>
										methodologyWithAdditionalInfo?.proposalFile &&
										downloadFile(methodologyWithAdditionalInfo?.proposalFile)
									}
								>
									<span>
										<BsFileEarmarkText />
									</span>

									<span>{getFileDownloadButtonLabel(methodologyWithAdditionalInfo)}</span>
								</Button>
							)}
							{canEditData && (
								<div>
									<Button
										variant="outline"
										className="mr-4 flex w-full items-center justify-center space-x-4 whitespace-nowrap"
										color="secondary"
										onClick={handleUploadDocument}
									>
										<span>Upload New Version</span>
									</Button>
									{documentFile && documentFile.size > MAX_FILE_SIZE && (
										<p className="mb-6 ml-4 text-xs text-red-500">Max file size is {getFileSize(MAX_FILE_SIZE)}.</p>
									)}
								</div>
							)}

							{props.isOriginator && props.methodology.status !== "APPROVED" && (
								<Button
									variant={props.publishDisabled || props.roundStatus === "RUNNING" ? "ghost" : "solid"}
									className="-ml-1 space-x-3 text-center font-medium"
									onClick={() => setPublishConfirmationModalOpen(true)}
									disabled={props.publishDisabled || props.roundStatus === "RUNNING"}
								>
									<span>{props.roundStatus === "RUNNING" ? "Submitted!" : "Submit To Publish"}</span>
								</Button>
							)}

							{isDocumentUploadModalOpen && (
								<DocumentUploadModal
									isModalOpen={isDocumentUploadModalOpen}
									handelModalClose={() => setIsDocumentUploadModalOpen(false)}
									setDocumentFile={setDocumentFile}
									isSaveChanges={true}
									fileSubmit={handleSubmit(onSubmit)}
									documentFile={documentFile}
									uniqueId={"upload-new-version"}
									trigger={trigger}
								/>
							)}

							{isPublishConfirmationModalOpen && (
								<PublishConfirmationModal
									isOpen={isPublishConfirmationModalOpen}
									closeModal={closeModal}
									methodology={methodology}
									onConfirm={onModalConfirm}
								/>
							)}
						</div>
					)}
				</form>
			</header>
		</>
	);
};

export default ConditionallyApprovedMethodologyHeader;

/**
 * @component
 * @name PublishConfirmationModal
 *
 * @description
 * The `PublishConfirmationModal` component is a modal that ask confirmation from user
 * if they are sure about submitting the methodology for publish
 *
 * @type {FC<PublishConfirmationModalProps>}
 * @param closeModal
 * @param methodology
 * @param onConfirm
 * @param {PublishConfirmationModalProps} props - Props for the PublishConfirmationModal component.
 * @returns {JSX.Element} The rendered PublishConfirmationModal component.
 */

const PublishConfirmationModal = ({ closeModal, onConfirm, ...props }: PublishConfirmationModalProps): JSX.Element => {
	return (
		<Modal
			{...props}
			closeModal={closeModal}
			title={<h1 className="w-100 p-10 pb-0 text-2xl">Submit to Publish?</h1>}
			showCloseButton={false}
			widthOverrideClass="max-w-3xl"
			opacityOverrideClass="bg-opacity-75"
		>
			<div className="flex flex-col items-start gap-10 p-10 pt-0">
				<div className="space-y-4 text-sm">
					<p>
						The methodology and public comment documents will now be sent to the expert panel for a final sign-off. If
						the panel agrees with the conditional approval, your methodology will be published on the OCP platform and
						available for project developers to use to propose new projects.
					</p>
					<p>
						If the panel changes their conditional agreement based on the public commentary and the methodology is
						rejected, you will be given the opportunity to communicate with the experts and attempt to figure out a
						solution that works for all parties. The OCP team will be on handle to help with this process.
					</p>
				</div>

				<div className="flex gap-4">
					<Button variant="outline" onClick={closeModal} className="flex items-center space-x-3 font-medium">
						<span>Go Back</span>
					</Button>

					<Button
						variant="solid"
						onClick={() => {
							onConfirm();
							closeModal();
						}}
						className="flex items-center space-x-3 font-medium text-black"
					>
						<span>Submit to Publish</span>
					</Button>
				</div>
			</div>
		</Modal>
	);
};

type PublishConfirmationModalProps = Omit<ComponentProps<typeof Modal>, "children" | "title"> & {
	methodology: MethodologyWithRoundDetails;
	onConfirm: () => void;
};
